// Drift: kontorets udlæg på Supabase (del af SupabaseApi – se supabase.ts). Reglerne ligger i 20261029200000_kontor3.sql:
// to forskellige personer godkender samme beløb (aldrig sit eget), afvis/spørg/bed om kvittering giver medarbejderen besked
// (notifikation + mail/SMS gennem beskeder), og "Penge sendt manuelt" kræver udlaeg.udbetal. Kvitteringerne ligger i den
// private bucket "steamer-billeder" og vises med midlertidige links. Medarbejderens egne udlæg ligger i sbSteamer.ts.
import { ApiFejl, type Api, type UdlaegVisning } from './api';
import type { Udlaeg } from '../../core/typer';
import { GODKENDELSER, tjekUdlaeg, type Aflaesning } from '../../core/udlaeg';
import { rensTekst } from '../../core/validering';
import type { SbKontekst } from './sbKatalog';

const BUCKET = 'steamer-billeder';
const LAGER = 'lager:';
const fejl = (b: string, f: Record<string, string> = {}) => new ApiFejl(b, f);
type Raa = Omit<Udlaeg, 'anmodning'> & { navn: string; rolle: UdlaegVisning['rolle']; anmodning: UdlaegVisning['anmodning'] };
const BLOKERER = ['mangler', 'ikke_kvittering', 'ulaeselig', 'kurs_mangler', 'dublet'];

export class SbUdlaeg {
  constructor(private readonly c: SbKontekst) {}
  private async rpc<T>(navn: string, args: Record<string, unknown> = {}): Promise<T> {
    return (await this.c.k(this.c.sb.rpc(navn, { k: await this.c.kraevKlient(), ...args }))) as T;
  }
  private async skriv<T>(navn: string, args: Record<string, unknown>): Promise<T> { const r = await this.rpc<T>(navn, args); this.c.skift(); return r; }

  udlaegListe: Api['udlaegListe'] = async () => {
    const [l, mig, kan] = await Promise.all([this.rpc<Raa[] | null>('udlaeg_liste'), this.rpc<string | null>('min_profil'), this.rpc<boolean>('har_evne', { evne: 'udlaeg.godkend' })]);
    const alle = l ?? [];
    const stier = [...new Set(alle.map((u) => u.billede).filter((b): b is string => !!b?.startsWith(LAGER)).map((b) => b.slice(LAGER.length)))];
    const links = new Map<string, string>();
    if (stier.length) for (const x of (await this.c.sb.storage.from(BUCKET).createSignedUrls(stier, 3600)).data ?? []) if (x.path && x.signedUrl) links.set(LAGER + x.path, x.signedUrl);
    return alle.map((u): UdlaegVisning => {
      // Kun udlæg indsendt FØR dette tæller – det første med kvitteringen er det rigtige, de næste er dubletter (som i testversionen)
      const andre = alle.filter((x) => x.id !== u.id && x.aflaest?.er_kvittering && x.tilstand !== 'afvist' && (x.oprettet < u.oprettet || (x.oprettet === u.oprettet && x.id < u.id)))
        .map((x) => ({ butik: x.aflaest!.butik, dato: x.aflaest!.dato, total: x.aflaest!.total, af: x.navn.split(' ')[0] }));
      const tjek = tjekUdlaeg(u.soegt, u.aflaest, andre);
      const harGodkendt = (u.godkendelser ?? []).some((g) => g.af === mig);
      return {
        ...u, billede: u.billede ? links.get(u.billede) ?? (u.billede.startsWith(LAGER) ? null : u.billede) : null, tjek, egen: u.profil_id === mig,
        mangler: Math.max(0, GODKENDELSER - (u.godkendelser?.length ?? 0)), harGodkendt,
        kanGodkende: u.tilstand === 'indsendt' && u.profil_id !== mig && !harGodkendt && kan && !BLOKERER.includes(tjek.status),
      };
    });
  };
  godkendUdlaeg: Api['godkendUdlaeg'] = async (id, beloeb) => {
    if (beloeb != null && !(Math.round(beloeb) > 0)) throw fejl('Ugyldigt beløb.');
    return { faerdig: await this.skriv<boolean>('godkend_udlaeg', { p_id: id, p_beloeb: beloeb == null ? null : Math.round(beloeb) }) };
  };
  afvisUdlaeg: Api['afvisUdlaeg'] = async (id, grund) => {
    if (rensTekst(grund ?? '', 300).trim().length < 3) throw fejl('Skriv hvorfor.', { grund: 'Skriv hvorfor' });
    await this.skriv('afvis_udlaeg', { p_id: id, p_grund: grund });
  };
  bedOmKvittering: Api['bedOmKvittering'] = async (id, grund) => {
    let g = rensTekst(grund ?? '', 200).trim();
    if (!g) g = (await this.udlaegListe()).find((u) => u.id === id)?.tjek.kort ?? '';
    return { link: await this.skriv<string>('bed_om_kvittering', { p_id: id, p_grund: g }) };
  };
  spoergUdlaeg: Api['spoergUdlaeg'] = async (id, tekst) => {
    if (rensTekst(tekst ?? '', 500).trim().length < 3) throw fejl('Skriv dit spørgsmål.', { tekst: 'Skriv dit spørgsmål' });
    await this.skriv('spoerg_udlaeg', { p_id: id, p_tekst: tekst });
  };
  udbetalUdlaeg: Api['udbetalUdlaeg'] = async (id) => { await this.skriv('udbetal_udlaeg', { p_id: id }); };

  // ---------- Linket til medarbejderen (uden login) ----------
  private async link(token: string) {
    return (await this.c.k(this.c.sb.rpc('udlaeg_link', { p_token: token }))) as Awaited<ReturnType<Api['udlaegLink']>> & { klient?: string };
  }
  udlaegLink: Api['udlaegLink'] = async (token) => { const { klient: _k, ...x } = await this.link(token); return x; };
  uploadKvittering: Api['uploadKvittering'] = async (token, billede, aflaest) => {
    const m = /^data:(image\/(jpeg|png|webp));base64,/.exec(billede);
    if (!m || billede.length > 6_000_000) throw fejl('Billedet kan ikke bruges.');
    const l = await this.link(token);
    if (!l.gyldig || !l.klient || !l.udlaeg) throw fejl('Linket er ikke aktivt.');
    const sti = `${l.klient}/kvittering/${token}/kvittering-${crypto.randomUUID()}.${m[2] === 'jpeg' ? 'jpg' : m[2]}`;
    const { error } = await this.c.sb.storage.from(BUCKET).upload(sti, await (await fetch(billede)).blob(), { contentType: m[1], upsert: false });
    if (error) throw fejl('Billedet kunne ikke gemmes. Tjek forbindelsen, og prøv igen.');
    const a: Aflaesning = { ...aflaest, kilde: 'manuel', sikkerhed: 0, total: aflaest.total ?? l.udlaeg.soegt };
    await this.c.k(this.c.sb.rpc('upload_kvittering_link', { p_token: token, p_sti: sti, p_aflaest: a }));
    return { tjek: tjekUdlaeg(l.udlaeg.soegt, a) };
  };
}
